import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useReportFilter } from "../context/ReportFilterContext";
import { getValidToken } from "../../../utils/authUtils";
import {
  formatCurrency,
  formatDate,
  formatText,
  formatTime12Hour,
  formatVehicleNumber,
} from "../../../utils/utils";
import { Loading } from "../../../components/Loading";
import { useNavigate } from "react-router-dom";

interface RentalJourneyIncomeRecord {
  id: string;
  incomeType: string | null;
  amount: number | null;
  limit: number | null;
  perKmRate: number | null;
  overLimitRate: number | null;
  gstPercentage: number | null;
  calculatedAmount: number;
  paidAmount: number;
  balanceAmount: number;
  remarks: string;
}

interface RentalJourneyExpenseRecord {
  id: string;
  expenseDate: string;
  expenseType: string;
  amount: number;
  paidAmount: number;
  balanceAmount: number;
  paidTo: string;
  location: string;
  contactNumber: string | null;
}

interface RentalJourneyItem {
  rentalJourneyId: string;
  vehicleInfo: {
    vehicleType: string;
    vehicleId: string;
    vehicleNumber: string;
    vehicleTypeDetail: string;
  };
  partyInfo: {
    partyId: string;
    partyName: string;
  };
  journeyDetails: {
    startDateTime: string;
    endDateTime: string | null;
    startLocation: string;
    endLocation: string;
    odometerReading: number;
    remarks: string | null;
  };
  income: {
    totalIncome: number;
    totalIncomeReceived: number;
    totalIncomeBalance: number;
    incomeByType: Record<
      string,
      {
        total: number;
        received: number;
        balance: number;
        records: RentalJourneyIncomeRecord[];
      }
    >;
    incomeRecords: RentalJourneyIncomeRecord[];
  };
  expenses: {
    totalExpense: number;
    totalExpensePaid: number;
    totalExpenseBalance: number;
    expenseByType: Record<
      string,
      {
        total: number;
        paid: number;
        balance: number;
        records: RentalJourneyExpenseRecord[];
      }
    >;
    expenseRecords: RentalJourneyExpenseRecord[];
  };
  summary: {
    totalIncome: number;
    totalExpense: number;
    netProfitLoss: number;
  };
  createdAt: string;
}

interface RentalJourneyPNLResponse {
  isSuccess: boolean;
  message: string;
  data: {
    success: boolean;
    data: {
      rentalJourneys: RentalJourneyItem[];
      summary: {
        totalJourneys: number;
        totalIncome: number;
        totalExpense: number;
        totalIncomeReceived: number;
        totalExpensePaid: number;
        totalIncomeBalance: number;
        totalExpenseBalance: number;
        netProfitLoss: number;
      } & Record<string, any>;
      pagination?: {
        currentPage: number;
        totalPages: number;
        totalRecords: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
      dateRange?: { start: string; end: string };
    };
  };
}

const RentalJourneyPNLReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const navigate = useNavigate();
  const {
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicleIds,
    selectedMarketLoadIds,
    selectedParties,
  } = filterState;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading, isFetching } = useQuery<RentalJourneyPNLResponse>({
    queryKey: [
      "rentalJourneyPNL",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicleIds,
      selectedMarketLoadIds,
      selectedParties,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/rental-journey-pnl/${timeFilter}`;
      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/rental-journey-pnl/custom`;
      }
      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      if (selectedVehicleIds && selectedVehicleIds.length > 0) {
        params.vehicleIds = selectedVehicleIds;
      }
      if (selectedMarketLoadIds && selectedMarketLoadIds.length > 0) {
        params.marketLoadIds = selectedMarketLoadIds;
      }
      if (selectedParties && selectedParties.length > 0) {
        params.partyIds = selectedParties;
      }
      // Add pagination parameters
      params.page = currentPage;
      params.limit = pageSize;

      const response = await axios.get(url, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  const journeys: RentalJourneyItem[] = data?.data?.data?.rentalJourneys || [];
  const summary = data?.data?.data?.summary;
  const pagination = data?.data?.data?.pagination;

  // Pagination handlers
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isLoading && <Loading fullScreen={false} message="Loading..." />}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing..." />
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Bookings</Typography>
            <Typography variant="h4" fontSize={15}>
              {summary?.totalJourneys ?? 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Income</Typography>
            <Typography variant="h4" fontSize={15} color="#4caf50">
              {formatCurrency(summary?.totalIncome ?? 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Total Expense</Typography>
            <Typography variant="h4" fontSize={15} color="#808080">
              {formatCurrency(summary?.totalExpense ?? 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography fontSize={12}>Net P&L</Typography>
            <Typography
              variant="h4"
              fontSize={15}
              sx={{
                color:
                  (summary?.netProfitLoss ?? 0) >= 0 ? "#4caf50" : "#f44336",
              }}
            >
              {formatCurrency(summary?.netProfitLoss ?? 0)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography fontSize={14} fontWeight={600} mb={2}>
              Bookings
            </Typography>
            {journeys.map((journey) => {
              const income = journey.income?.totalIncome ?? 0;
              const expense = journey.expenses?.totalExpense ?? 0;
              const pnl = journey.summary?.netProfitLoss ?? income - expense;
              return (
                <Accordion
                  key={journey.rentalJourneyId}
                  sx={{
                    mb: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px !important",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      margin: "8px 0",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#666" }} />}
                    sx={{
                      minHeight: "48px !important",
                      py: 1,
                      px: 2,
                      backgroundColor: "#fafafa",
                      borderRadius: "8px",
                      "&.Mui-expanded": {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        borderBottom: "1px solid #e0e0e0",
                      },
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                        margin: "8px 0 !important",
                        "&.Mui-expanded": {
                          margin: "8px 0 !important",
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "12%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/rental/${journey.rentalJourneyId}/logs`);
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "primary.main",
                            textDecoration: "underline",
                            lineHeight: 1.2,
                          }}
                        >
                          {formatDate(journey.journeyDetails.startDateTime)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "9px",
                            color: "#666",
                            lineHeight: 1.1,
                          }}
                        >
                          {formatTime12Hour(
                            journey.journeyDetails.startDateTime
                          )}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "12%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#333",
                            lineHeight: 1.2,
                          }}
                        >
                          {journey.journeyDetails.endDateTime
                            ? formatDate(journey.journeyDetails.endDateTime)
                            : "Ongoing"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "9px",
                            color: "#666",
                            lineHeight: 1.1,
                          }}
                        >
                          {journey.journeyDetails.endDateTime
                            ? formatTime12Hour(
                                journey.journeyDetails.endDateTime
                              )
                            : "-"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "14%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#1976d2",
                            px: 1,
                            py: 0.5,
                            borderRadius: "12px",
                            lineHeight: 1,
                          }}
                        >
                          {formatVehicleNumber(
                            journey.vehicleInfo.vehicleNumber
                          )}
                          {journey.vehicleInfo.vehicleType === "MARKET_LOAD" &&
                            " (ML)"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "14%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 500,
                            color: "#333",
                            px: 1,
                            py: 0.5,
                            borderRadius: "8px",
                            lineHeight: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {journey.partyInfo.partyName}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "20%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "#666",
                            lineHeight: 1.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {journey.journeyDetails.startLocation} →{" "}
                          {journey.journeyDetails.endLocation}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "9%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "9px",
                            color: "#666",
                            lineHeight: 1,
                          }}
                        >
                          Income
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          {formatCurrency(income)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "9%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "9px",
                            color: "#666",
                            lineHeight: 1,
                          }}
                        >
                          Expense
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#808080",
                            lineHeight: 1.2,
                          }}
                        >
                          {formatCurrency(expense)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: "10%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "9px",
                            color: "#666",
                            lineHeight: 1,
                          }}
                        >
                          P&L
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: pnl >= 0 ? "#4caf50" : "#f44336",
                            backgroundColor: pnl >= 0 ? "#e8f5e8" : "#ffebee",
                            px: 1,
                            py: 0.5,
                            borderRadius: "8px",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(pnl)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      pt: 2,
                      pb: 1,
                      px: 2,
                      backgroundColor: "#fafafa",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#333",
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 3,
                              height: 16,
                              backgroundColor: "#4caf50",
                              borderRadius: 1,
                            }}
                          />
                          Income Details
                        </Typography>
                        {journey.income?.incomeRecords?.length === 0 ? (
                          <Typography
                            color="text.secondary"
                            fontSize={11}
                            sx={{
                              fontStyle: "italic",
                              textAlign: "center",
                              py: 2,
                              backgroundColor: "#f9f9f9",
                              borderRadius: 1,
                            }}
                          >
                            No income records
                          </Typography>
                        ) : (
                          <TableContainer
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                            }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      py: 1,
                                    }}
                                  >
                                    Type
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      py: 1,
                                    }}
                                  >
                                    Amount
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      py: 1,
                                    }}
                                  >
                                    GST %
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      py: 1,
                                    }}
                                  >
                                    Calculated
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      py: 1,
                                    }}
                                  >
                                    Paid
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      py: 1,
                                    }}
                                  >
                                    Balance
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {journey.income?.incomeRecords?.map(
                                  (rec, index) => (
                                    <TableRow key={rec.id}>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {/* TODO : Instead of amount, use perKmRate */}
                                        {rec.incomeType ?? "-"}
                                        {rec.incomeType === "perkm" && (
                                          <>
                                            <br />({rec.limit} km * {rec.amount}
                                            )
                                          </>
                                        )}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {formatCurrency(rec.calculatedAmount)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {rec.gstPercentage != null
                                          ? `${rec.gstPercentage}%`
                                          : "-"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                        }}
                                      >
                                        {formatCurrency(rec.calculatedAmount)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                        }}
                                      >
                                        {formatCurrency(rec.paidAmount)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                        }}
                                      >
                                        {formatCurrency(rec.balanceAmount)}
                                      </TableCell>
                                    </TableRow>
                                  )
                                )}
                                {/* Total Row */}
                                <TableRow>
                                  <TableCell
                                    colSpan={3}
                                    sx={{
                                      fontSize: 11,
                                      py: 1,
                                    }}
                                  >
                                    Total Income
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      py: 1,
                                    }}
                                  >
                                    {formatCurrency(
                                      journey.income?.totalIncome ?? 0
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      py: 1,
                                    }}
                                  >
                                    {formatCurrency(
                                      journey.income?.totalIncomeReceived ?? 0
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      py: 1,
                                    }}
                                  >
                                    {formatCurrency(
                                      journey.income?.totalIncomeBalance ?? 0
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#333",
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 3,
                              height: 16,
                              backgroundColor: "#808080",
                              borderRadius: 1,
                            }}
                          />
                          Expense Details
                        </Typography>
                        {journey.expenses?.expenseRecords?.length === 0 ? (
                          <Typography
                            color="text.secondary"
                            fontSize={11}
                            sx={{
                              fontStyle: "italic",
                              textAlign: "center",
                              py: 2,
                              backgroundColor: "#f9f9f9",
                              borderRadius: 1,
                            }}
                          >
                            No expense records
                          </Typography>
                        ) : (
                          <TableContainer
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                            }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      py: 1,
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Type
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1,
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Date
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1,
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Paid To
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1,
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Location
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Amount
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1,
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Paid
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1,
                                      fontSize: 10,
                                      fontWeight: 600,
                                    }}
                                  >
                                    Balance
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {journey.expenses?.expenseRecords?.map(
                                  (rec, index) => (
                                    <TableRow key={rec.id}>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {formatText(rec.expenseType) || "-"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {formatDate(rec.expenseDate)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {rec.paidTo || "-"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {rec.location || "-"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {formatCurrency(rec.amount)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {formatCurrency(rec.paidAmount)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 10,
                                          py: 0.8,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {formatCurrency(rec.balanceAmount)}
                                      </TableCell>
                                    </TableRow>
                                  )
                                )}
                                {/* Total Row */}
                                <TableRow>
                                  <TableCell
                                    colSpan={4}
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      py: 1,
                                    }}
                                  >
                                    Total Expense
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      py: 1,
                                    }}
                                  >
                                    {formatCurrency(
                                      journey.expenses?.totalExpense ?? 0
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      py: 1,
                                    }}
                                  >
                                    {formatCurrency(
                                      journey.expenses?.totalExpensePaid ?? 0
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      py: 1,
                                    }}
                                  >
                                    {formatCurrency(
                                      journey.expenses?.totalExpenseBalance ?? 0
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            {journeys.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No Booking P&L data available
              </Typography>
            )}

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 80, fontSize: "0.75rem" }}
                  >
                    <Select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      displayEmpty
                      size="small"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      <MenuItem value={20} sx={{ fontSize: "0.75rem" }}>
                        20
                      </MenuItem>
                      <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>
                        50
                      </MenuItem>
                      <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>
                        100
                      </MenuItem>
                      <MenuItem value={200} sx={{ fontSize: "0.75rem" }}>
                        200
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    {`${(pagination.currentPage - 1) * pageSize + 1}-${Math.min(
                      pagination.currentPage * pageSize,
                      pagination.totalRecords
                    )} of ${pagination.totalRecords}`}
                  </Typography>
                </Box>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RentalJourneyPNLReport;
